if (!customElements.get('rerack-inventory-status')) {
  class RerackInventoryStatus extends HTMLElement {
    connectedCallback() {
      this.currentVariantId = this.dataset.variantId;
      this.loadInventory(this.currentVariantId);

      this.variantInput = this.closest('product-form')
        ?.querySelector('.product-variant-id');

      if (this.variantInput) {
        this.observeVariantChanges();
      }
    }

    disconnectedCallback() {
      if (this.variantObserver) {
        this.variantObserver.disconnect();
      }
    }

    observeVariantChanges() {
      this.variantObserver = new MutationObserver(() => {
        const newVariantId = this.variantInput.value;

        if (
          newVariantId &&
          newVariantId !== this.currentVariantId
        ) {
          this.currentVariantId = newVariantId;
          this.dataset.variantId = newVariantId;
          this.loadInventory(newVariantId);
        }
      });

      this.variantObserver.observe(this.variantInput, {
        attributes: true,
        attributeFilter: ['value'],
      });

      this.variantInput.addEventListener('change', () => {
        const newVariantId = this.variantInput.value;

        if (
          newVariantId &&
          newVariantId !== this.currentVariantId
        ) {
          this.currentVariantId = newVariantId;
          this.dataset.variantId = newVariantId;
          this.loadInventory(newVariantId);
        }
      });
    }

    async loadInventory(variantId) {
      const pickupOnly = this.dataset.pickupOnly === 'true';

      if (!variantId) {
        this.innerHTML = '';
        return;
      }

      this.innerHTML = `
        <div class="rerack-inventory-status__loading">
          Checking availability...
        </div>
      `;

      try {
        const response = await fetch(
          `/apps/inventory?variant_id=${encodeURIComponent(variantId)}`,
          {
            headers: {
              Accept: 'application/json',
            },
          }
        );

        if (!response.ok) {
          throw new Error(
            `Inventory request failed: ${response.status}`
          );
        }

        const data = await response.json();

        if (!data.success) {
          throw new Error(
            data.error || 'Inventory request failed'
          );
        }

        this.renderInventory(data, pickupOnly);
      } catch (error) {
        console.error('ReRack inventory error:', error);

        this.dataset.inventoryPickupOnly = 'false';

        this.innerHTML = `
          <div class="rerack-inventory-status__error">
            Availability currently unavailable.
          </div>
        `;
      }
    }

    statusLabel(status) {
      if (status === 'in_stock') return 'In Stock';
      if (status === 'low_stock') return 'Low Stock';
      return 'Out of Stock';
    }

    statusClass(status) {
      if (status === 'in_stock') return 'is-in-stock';
      if (status === 'low_stock') return 'is-low-stock';
      return 'is-out-of-stock';
    }

    renderRow(type, item) {
      return `
        <div class="rerack-inventory-status__row">
          <div class="rerack-inventory-status__details">
            <strong>${type}:</strong>
            <span>${item.location}</span>
          </div>

          <span class="rerack-inventory-status__stock ${this.statusClass(item.status)}">
            ${this.statusLabel(item.status)}
          </span>
        </div>
      `;
    }

    renderInventory(data, pickupOnly) {
      const shipping = data.shipping;

      const pickup = Array.isArray(data.pickup)
        ? data.pickup
        : [];

      /*
       * Determine whether this variant is effectively pickup-only
       * because of inventory.
       *
       * Warehouse must have NO available stock,
       * while at least one pickup location DOES have stock.
       */
      const shippingAvailable =
        shipping &&
        shipping.status !== 'out_of_stock';

      const pickupAvailable = pickup.some(
        (location) => location.status !== 'out_of_stock'
      );

      const inventoryPickupOnly =
        !shippingAvailable && pickupAvailable;

      this.dataset.inventoryPickupOnly =
        inventoryPickupOnly ? 'true' : 'false';

      let html = '';

      /*
       * Products explicitly tagged pickup-only keep the existing
       * permanent pickup-only message.
       */
      if (pickupOnly) {
        html += `
          <div class="rerack-inventory-status__notice">
            <strong>In-Store Pickup Only</strong>
            <span>This item is not available for shipping.</span>
          </div>
        `;
      } else if (shipping) {
        html += this.renderRow('Shipping', shipping);
      }

      pickup.forEach((location) => {
        html += this.renderRow('Pickup', location);
      });

      this.innerHTML = html;
    }
  }

  customElements.define(
    'rerack-inventory-status',
    RerackInventoryStatus
  );
}


/*
 * Pickup warning when an item cannot currently ship
 * from the warehouse but is available at a pickup location.
 *
 * Products explicitly tagged "pickup-only" do NOT get this popup.
 */
document.addEventListener(
  'click',
  (event) => {
    const addButton = event.target.closest(
      '.product-form__submit[name="add"]'
    );

    if (!addButton) return;

    const productForm = addButton.closest('product-form');

    if (!productForm) return;

    const inventoryStatus = productForm.querySelector(
  'rerack-inventory-status'
);

const modal = productForm.querySelector(
  '.rerack-pickup-modal'
);

    if (!inventoryStatus || !modal) return;

    /*
     * Products that are intentionally pickup-only already tell
     * the customer that on the product page.
     * Do NOT show the popup for those.
     */
    const taggedPickupOnly =
      inventoryStatus.dataset.pickupOnly === 'true';

    if (taggedPickupOnly) return;

    /*
     * This becomes true when:
     * Warehouse = Out of Stock
     * AND
     * Portland/Scappoose = In Stock or Low Stock
     */
    const inventoryPickupOnly =
      inventoryStatus.dataset.inventoryPickupOnly === 'true';

    if (!inventoryPickupOnly) return;

    /*
     * If the customer already acknowledged the warning,
     * allow Dawn's next click to proceed normally.
     */
    if (addButton.dataset.pickupConfirmed === 'true') {
      addButton.dataset.pickupConfirmed = 'false';
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    modal.hidden = false;

    const continueButton = modal.querySelector(
      '.rerack-pickup-modal__continue'
    );

    const closeButton = modal.querySelector(
      '.rerack-pickup-modal__close'
    );

    const backdrop = modal.querySelector(
      '.rerack-pickup-modal__backdrop'
    );

    const closeModal = () => {
      modal.hidden = true;
    };

    closeButton?.addEventListener(
      'click',
      closeModal,
      { once: true }
    );

    backdrop?.addEventListener(
      'click',
      closeModal,
      { once: true }
    );

    continueButton?.addEventListener(
      'click',
      () => {
        modal.hidden = true;

        addButton.dataset.pickupConfirmed = 'true';

        addButton.click();
      },
      { once: true }
    );
  },
  true
);